/**
 * EngagementProgressingDetailPanel
 *
 * "View details for (X)" panel matching the uploaded screenshot: client
 * code/name/engagement code+name header row, a per-staff table (name,
 * rate, hours, time charges, status), a Total row, and a Download link.
 * Rendered once per checked checkbox -- selecting more checkboxes stacks
 * more of these panels below the main table.
 */
import { Download } from 'lucide-react';

const CATEGORY_LABELS = {
  pending: 'PENDING_TRS',
  approved: 'APPROVED_TRS',
  booked: 'BOOKED_SSS',
};

const currency = (n) => n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

/**
 * @param {{
 *   category: 'pending'|'approved'|'booked',
 *   row: { clientCode: string, clientName: string, engagementCode: string, engagementName: string },
 *   staff: { name: string, rate: number, hours: number, charges: number, status: string }[],
 * }} props
 */
export const EngagementProgressingDetailPanel = ({ category, row, staff }) => {
  const totalHours = staff.reduce((sum, s) => sum + s.hours, 0);
  const totalCharges = staff.reduce((sum, s) => sum + s.charges, 0);

  return (
    <div className="rounded-lg border border-edge bg-surface">
      <div className="flex items-center justify-between border-b border-edge px-4 py-3">
        <p className="text-sm font-semibold text-primary">
          View details for ({CATEGORY_LABELS[category]})
        </p>
        <button
          type="button"
          className="flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
        >
          <Download className="h-3.5 w-3.5" />
          Download
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-edge text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-2 font-medium">Client code</th>
              <th className="px-4 py-2 font-medium">Name Client</th>
              <th className="px-4 py-2 font-medium">Engagement code</th>
              <th className="px-4 py-2 font-medium">Engagement name</th>
              <th className="px-4 py-2 font-medium">Names of staff</th>
              <th className="px-4 py-2 font-medium">Rate</th>
              <th className="px-4 py-2 font-medium">Total Hours</th>
              <th className="px-4 py-2 font-medium">Total time charges</th>
              <th className="px-4 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-muted">
                  No entries for this period.
                </td>
              </tr>
            )}
            {staff.map((s, i) => (
              <tr key={s.name} className="border-b border-edge/60 text-primary hover:bg-surface-hover">
                {i === 0 && (
                  <>
                    <td className="px-4 py-2 align-top" rowSpan={staff.length}>{row.clientCode}</td>
                    <td className="px-4 py-2 align-top" rowSpan={staff.length}>{row.clientName}</td>
                    <td className="px-4 py-2 align-top" rowSpan={staff.length}>{row.engagementCode}</td>
                    <td className="px-4 py-2 align-top" rowSpan={staff.length}>{row.engagementName}</td>
                  </>
                )}
                <td className="px-4 py-2 text-accent">{s.name}</td>
                <td className="px-4 py-2">{s.rate}</td>
                <td className="px-4 py-2">{s.hours}</td>
                <td className="px-4 py-2">{currency(s.charges)}</td>
                <td className="px-4 py-2 text-muted">{s.status}</td>
              </tr>
            ))}
          </tbody>
          {staff.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-edge font-semibold text-primary">
                <td className="px-4 py-2" colSpan={6}>
                  Total:
                </td>
                <td className="px-4 py-2">{totalHours}</td>
                <td className="px-4 py-2">{currency(totalCharges)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};
